import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, shortDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill } from "../components/UI";

export default function RegoInspections() {
  const [data, setData] = useState(null);
  const [searchParams] = useSearchParams();
  const targetUnit = searchParams.get("unit");
  const targetRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/rego-inspections").then(r => setData(r.data)); }, []);
  useEffect(() => {
    if (targetUnit && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [data, targetUnit]);

  if (!data) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  const { counts, rows } = data;

  return (
    <div data-testid="rego-inspections-page">
      <PageHeader
        title="Rego &amp; inspections"
        subtitle="What's due, what's out of date, what still has months to go"
      />

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(6, minmax(0,1fr))"}}>
        <KpiCard tone={counts.rego_overdue > 0 ? "red" : "neutral"} number={counts.rego_overdue} label="Rego ran out already" testid="ri-kpi-rego-out" />
        <KpiCard tone="amber" number={counts.rego_soon} label="Rego due in 30 days" testid="ri-kpi-rego-soon" />
        <KpiCard tone={counts.cs_overdue > 0 ? "red" : "neutral"} number={counts.cs_overdue} label="CraneSafe overdue" testid="ri-kpi-cs-out" />
        <KpiCard tone="amber" number={counts.cs_soon} label="CraneSafe due in 30 days" testid="ri-kpi-cs-soon" />
        <KpiCard tone={counts.plant_overdue > 0 ? "red" : "neutral"} number={counts.plant_overdue} label="Plant reg overdue" testid="ri-kpi-plant-out" />
        <KpiCard tone="amber" number={counts.plant_soon} label="Plant reg due in 30 days" testid="ri-kpi-plant-soon" />
      </div>

      {targetUnit && (
        <div className="ach-card" style={{marginBottom:12, background:"var(--ach-amber-tint)", borderColor:"var(--ach-amber)"}} data-testid="ri-focus-banner">
          <div style={{fontSize:13, color:"var(--ach-text-1)"}}>
            Focused on <strong>{targetUnit}</strong>. Book its CraneSafe / rego / 10-year / plant reg through the usual channels — this list is your working copy.
          </div>
        </div>
      )}

      <div className="ach-card">
        <h2 className="ach-card__title">Every machine — sorted so the worst is on top</h2>
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Model</th>
                <th>Owned by</th>
                <th>Where</th>
                <th>Rego expiry</th>
                <th>CraneSafe due</th>
                <th>10-year major</th>
                <th>Plant reg</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isTarget = targetUnit && r.unit_number === targetUnit;
                return (
                <tr key={r.id} ref={isTarget ? targetRef : null} data-testid={`ri-row-${r.unit_number}`}
                    style={isTarget ? {background:"var(--ach-amber-tint)", outline:"2px solid var(--ach-amber)"} : undefined}>
                  <td>
                    <button
                      type="button"
                      onClick={()=>nav(`/machine-details?unit=${encodeURIComponent(r.unit_number)}`)}
                      data-testid={`ri-unit-link-${r.unit_number}`}
                      className="unit-num"
                      style={{background:"none", border:"none", padding:0, cursor:"pointer", color:"var(--ach-maroon)", fontWeight:700, fontSize:"inherit", textDecoration:"underline"}}>
                      {r.unit_number}
                    </button>
                    <div className="subtitle">{r.status.replace(/-/g, " ")}</div>
                  </td>
                  <td>{r.make} {r.model_name}</td>
                  <td>{r.owning_company}</td>
                  <td>{r.location || "—"}</td>
                  <td><div>{r.rego.expiry ? shortDate(r.rego.expiry) : "—"}</div><StatusPill tone={r.rego.tone}>{r.rego.label}</StatusPill></td>
                  <td><div>{r.cranesafe.due ? shortDate(r.cranesafe.due) : "—"}</div><StatusPill tone={r.cranesafe.tone}>{r.cranesafe.label}</StatusPill></td>
                  <td><div>{r.ten_year.due ? shortDate(r.ten_year.due) : "—"}</div><StatusPill tone={r.ten_year.tone}>{r.ten_year.label}</StatusPill></td>
                  <td>
                    <div>{r.plant_reg.number ? <strong>{r.plant_reg.number}</strong> : <span style={{color:"var(--ach-text-2)"}}>—</span>}</div>
                    <div>{r.plant_reg.expiry ? shortDate(r.plant_reg.expiry) : ""}</div>
                    <StatusPill tone={r.plant_reg.tone}>{r.plant_reg.label}</StatusPill>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
